import {
    type ApolloQueryResult,
    type OperationVariables,
} from '@apollo/client';
import {
    type DocumentParameter,
    type OptionsParameter,
    type UseQueryReturn,
    type VariablesParameter,
} from '@vue/apollo-composable/dist/useQuery.d.ts';

type UseAwaitQueryReturn<TResult, TVariable extends OperationVariables> = Omit<
    UseQueryReturn<TResult, TVariable>,
    'onResult' | 'onError' | 'error' | 'result'
> & { result: () => Promise<ApolloQueryResult<TResult>> };

export function useAwaitQuery<TResult = any>(
    document: DocumentParameter<TResult, undefined>
): UseAwaitQueryReturn<TResult, Record<string, never>>;
/**
 * Use a query that has optional variables but not options
 */
export function useAwaitQuery<
    TResult = any,
    TVariables extends OperationVariables = OperationVariables,
>(
    document: DocumentParameter<TResult, TVariables>
): UseAwaitQueryReturn<TResult, TVariables>;
/**
 * Use a query that has required variables but not options
 */
export function useAwaitQuery<
    TResult = any,
    TVariables extends OperationVariables = OperationVariables,
>(
    document: DocumentParameter<TResult, TVariables>,
    variables: VariablesParameter<TVariables>
): UseAwaitQueryReturn<TResult, TVariables>;
/**
 * Use a query that requires options but not variables.
 */
export function useAwaitQuery<TResult = any>(
    document: DocumentParameter<TResult, undefined>,
    variables: undefined | null,
    options: OptionsParameter<TResult, Record<string, never>>
): UseAwaitQueryReturn<TResult, Record<string, never>>;
/**
 * Use a query that requires variables and options.
 */
export function useAwaitQuery<
    TResult = any,
    TVariables extends OperationVariables = OperationVariables,
>(
    document: DocumentParameter<TResult, TVariables>,
    variables: VariablesParameter<TVariables>,
    options: OptionsParameter<TResult, TVariables>
): UseAwaitQueryReturn<TResult, TVariables>;
export function useAwaitQuery(document: any, variables?: any): any {
    const enableQuery = ref(false);
    const {
        onResult,
        loading,
        onError,
        variables: rVariables,
        document: rDocument,
        options,
        fetchMore,
        subscribeToMore,
        query,
        networkStatus,
        refetch,
        stop,
        start,
        restart,
    } = useQuery(document, variables as any, () => ({
        enabled: enableQuery.value,
        notifyOnNetworkStatusChange: false,
    }));

    const apolloQuery = () =>
        new Promise<ApolloQueryResult<any>>((resolve, reject) => {
            enableQuery.value = true;
            onResult(res => {
                if (!res.loading) {
                    enableQuery.value = false;
                    resolve(res);
                }
            });
            onError(err => {
                enableQuery.value = false;
                reject(err);
            });
        });

    return {
        result: apolloQuery,
        loading,
        variables: rVariables,
        document: rDocument,
        options,
        fetchMore,
        subscribeToMore,
        query,
        networkStatus,
        refetch,
        restart,
        start,
        stop,
    } as any;
}
